import { useEffect, useRef } from "react";
import { getVersion } from "../api/Api";
import { VERSION, VERSION_SET } from "../version";
import { UpdateSW } from "../lib/update_sw";
import { shouldUpdateServiceWorker } from "../lib/versionComparison";

/**
 * A hook that checks if the client version should trigger a service worker
 * update. Versions are `MAJOR.MINOR.THIRD.PATCH-BRANCH` (see
 * `shouldUpdateServiceWorker`).
 *
 * Update Rules:
 * - Update if MAJOR, MINOR, or BRANCH changes.
 * - Update if the PATCH (4th) segment changes while the rest stay the same.
 * - DON'T update if only the THIRD segment changes.
 *
 * Performs the check on mount and periodically thereafter.
 */
// Standalone deployment (app.parrotapp.com): the client is versioned
// independently of the backend (date-sha, stamped by client-deploy.yml), so
// comparing against the backend's version is meaningless. Instead compare
// against this origin's own /version (served by nginx next to the bundle):
// any difference means a newer bundle is deployed — update.
const STANDALONE = !!import.meta.env.VITE_PARROT_TARGET?.startsWith("app-");

const getOwnOriginVersion = async (): Promise<string | null> => {
  try {
    const res = await fetch("/version", { cache: "no-store" });
    if (!res.ok) return null;
    return (await res.text()).trim();
  } catch {
    return null;
  }
};

export const useVersionChecker = () => {
  // Flag to prevent multiple simultaneous version checks
  const isCheckingRef = useRef(false);

  useEffect(() => {
    const checkVersion = async () => {
      // Skip if already checking
      if (isCheckingRef.current) return;
      // only do this for staging and for production
      if (!VERSION_SET) return;

      try {
        isCheckingRef.current = true;
        // Get the deployed version (own origin when standalone, backend otherwise)
        const serverVersion = STANDALONE
          ? await getOwnOriginVersion()
          : await getVersion();

        // Standalone compares exact versions; the embedded build keeps the
        // MAJOR/MINOR/THIRD/PATCH-BRANCH service-worker comparison rules.
        const needsUpdate = STANDALONE
          ? !!serverVersion && serverVersion !== VERSION
          : !!serverVersion && shouldUpdateServiceWorker(VERSION, serverVersion);

        if (needsUpdate) {
          console.log(
            `Version update required: client ${VERSION} -> server ${serverVersion}; updating service worker...`,
          );
          await UpdateSW();
          // The page should refresh soon after UpdateSW is called
        } else if (serverVersion && serverVersion !== VERSION) {
          console.log(
            "Version difference detected but no update needed (third number change only)"
          );
        }
      } catch (error) {
        console.error("Error checking version:", error);
      } finally {
        isCheckingRef.current = false;
      }
    };

    // Check immediately on mount
    checkVersion();

    const oneSecond = 1000;
    const oneMinute = 60 * oneSecond;
    // Set up periodic checks
    const intervalId = setInterval(() => {
      checkVersion();
    }, oneMinute); // Check every minute if it's time for a version check

    return () => {
      clearInterval(intervalId);
    };
  }, []);
};

export default useVersionChecker;
